import { BuildingReadModel, SpaceHierarchyReadModel } from '@client/api';
import dayjs, { Dayjs } from 'dayjs';
import { DateRangePresetKey } from '../components/DashboardFilters/useDefaultDateRangePresets';
import { useFilterSearchQuery } from '../../../hooks/useFilterSearchQuery';
import { useFilterInitialValue } from '../../../hooks/useFilterInitialValue';
import { createContext, useContext, useState, ReactNode, useMemo, useEffect, useRef } from 'react';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectSelectedTenant } from '../../../redux/session/session.slice';
import { useSearchParams } from 'react-router-dom';

interface DashboardFiltersContextType {
  selectedBuildings: BuildingReadModel[];
  selectedSpaceIds: string[];
  setSelectedBuildings: (buildings: BuildingReadModel[]) => void;
  selectedSpaces: SpaceHierarchyReadModel[];
  setSelectedSpaces: (spaces: SpaceHierarchyReadModel[]) => void;
  selectedDateFrom: string;
  setSelectedDateFrom: (date: Dayjs | null) => void;
  selectedDateTo: string;
  setSelectedDateTo: (date: Dayjs | null) => void;
  selectedDateFromIso: string;
  selectedDateToIso: string;
  activePresetKey: DateRangePresetKey | null;
  setActivePresetKey: (key: DateRangePresetKey | null) => void;
}

const DashboardFiltersContext = createContext<DashboardFiltersContextType | undefined>(undefined);

const buildingParamKey = 'b';

interface DashboardFiltersProviderProps {
  dateParamsPrefix?: string;
  startDateParamKey?: string;
  endDateParamKey?: string;
  children: ReactNode;
}

export function DashboardFiltersProvider({
  children,
  dateParamsPrefix = '',
  startDateParamKey = 'startDate',
  endDateParamKey = 'endDate',
}: DashboardFiltersProviderProps) {
  const startDateParamWithPrefix = dateParamsPrefix ? `${dateParamsPrefix}${startDateParamKey}` : startDateParamKey;
  const endDateParamWithPrefix = dateParamsPrefix ? `${dateParamsPrefix}${endDateParamKey}` : endDateParamKey;

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBuildings, setSelectedBuildings] = useState<BuildingReadModel[]>([]);
  const [selectedSpaces, setSelectedSpaces] = useState<SpaceHierarchyReadModel[]>([]);
  const [selectedDateFrom, setSelectedDateFrom] = useState<Dayjs | null>(() => {
    const param = searchParams.get(startDateParamWithPrefix);
    return param ? dayjs(param).startOf('day') : null;
  });
  const [selectedDateTo, setSelectedDateTo] = useState<Dayjs | null>(() => {
    const param = searchParams.get(endDateParamWithPrefix);
    return param ? dayjs(param).endOf('day') : null;
  });
  const [activePresetKey, setActivePresetKey] = useState<DateRangePresetKey | null>(null);

  const { updateFilterParam } = useFilterSearchQuery(buildingParamKey);
  // Track selected tenant
  const selectedTenant = useAppSelector(selectSelectedTenant);
  const tenantIdRef = useRef<string | null>(null);

  // Initialize tenant ref on mount
  useEffect(() => {
    const currentTenantId =
      selectedTenant && typeof selectedTenant === 'object' && 'id' in selectedTenant ? selectedTenant.id : null;
    tenantIdRef.current = currentTenantId;
  }, []);

  // Reset filters when tenant changes
  useEffect(() => {
    const currentTenantId =
      selectedTenant && typeof selectedTenant === 'object' && 'id' in selectedTenant ? selectedTenant.id : null;

    // Only reset if tenant actually changed (not on initial mount)
    if (tenantIdRef.current !== null && tenantIdRef.current !== currentTenantId) {
      // Clear state
      setSelectedBuildings([]);
      setSelectedDateFrom(null);
      setSelectedDateTo(null);

      // Clear URL parameters
      setSearchParams({});
    }

    tenantIdRef.current = currentTenantId;
  }, [selectedTenant, setSearchParams]);

  useFilterInitialValue(buildingParamKey, (values) => {
    setSelectedBuildings(values.map(([id, name]) => ({ id, name }) as BuildingReadModel));
  });

  const handleSelectBuildings = (buildings: BuildingReadModel[]) => {
    setSelectedBuildings(buildings);
    if (buildings.length === 0) {
      updateFilterParam('');
    } else {
      updateFilterParam(buildings.map((building) => `${building.id}:${building.name}`).join(','));
    }
  };

  const handleSetDateFrom = (date: Dayjs | null) => {
    setSelectedDateFrom(date);
    setSearchParams((prev) => {
      if (date) {
        prev.set(startDateParamWithPrefix, date.format('YYYY-MM-DD'));
      } else {
        prev.delete(startDateParamWithPrefix);
      }
      return prev;
    });
  };

  const handleSetDateTo = (date: Dayjs | null) => {
    setSelectedDateTo(date);
    setSearchParams((prev) => {
      if (date) {
        prev.set(endDateParamWithPrefix, date.format('YYYY-MM-DD'));
      } else {
        prev.delete(endDateParamWithPrefix);
      }
      return prev;
    });
  };

  const selectedSpaceIds = useMemo(() => {
    if (selectedSpaces.length > 0) {
      return selectedSpaces.map((space) => space.id);
    }
    if (selectedBuildings.length > 0) {
      return selectedBuildings.map((building) => building.id);
    }
    return [];
  }, [selectedBuildings, selectedSpaces]);

  return (
    <DashboardFiltersContext.Provider
      value={{
        selectedDateFromIso: selectedDateFrom?.toISOString() || '',
        selectedDateToIso: selectedDateTo?.toISOString() || '',
        selectedBuildings,
        selectedSpaceIds,
        setSelectedBuildings: handleSelectBuildings,
        selectedSpaces,
        setSelectedSpaces,
        selectedDateFrom: selectedDateFrom?.format('YYYY-MM-DD') || '',
        setSelectedDateFrom: handleSetDateFrom,
        selectedDateTo: selectedDateTo?.format('YYYY-MM-DD') || '',
        setSelectedDateTo: handleSetDateTo,
        activePresetKey,
        setActivePresetKey,
      }}
    >
      {children}
    </DashboardFiltersContext.Provider>
  );
}

export function useDashboardFilters() {
  const context = useContext(DashboardFiltersContext);
  if (context === undefined) {
    throw new Error('useDashboardFilters must be used within a DashboardFiltersProvider');
  }
  return context;
}
